import React, { useEffect, useState } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import apiService from '@/api-service';

interface BepalingFiltersProps {
    hoofdstukFilter: string;
    paragraafFilter: string;
    artikelFilter: string;
    lidFilter: string;
    onHoofdstukChange: (value: string) => void;
    onParagraafChange: (value: string) => void;
    onArtikelChange: (value: string) => void;
    onLidChange: (value: string) => void;
}

interface FilterOption {
    nummer: string;
    titel?: string;
}

const BepalingFilters: React.FC<BepalingFiltersProps> = ({
    hoofdstukFilter,
    paragraafFilter,
    artikelFilter,
    lidFilter,
    onHoofdstukChange,
    onParagraafChange,
    onArtikelChange,
    onLidChange
}) => {
    const [hoofdstukken, setHoofdstukken] = useState<FilterOption[]>([]);
    const [paragrafen, setParagrafen] = useState<FilterOption[]>([]);
    const [artikelen, setArtikelen] = useState<FilterOption[]>([]);
    const [leden, setLeden] = useState<FilterOption[]>([]);

    // Hoofdstukken: altijd alle opties, eenmalig laden
    useEffect(() => {
        apiService.get('/bepaling/hoofdstukken/')
            .then(response => setHoofdstukken(response.data))
            .catch(err => console.error('Error loading hoofdstukken:', err));
    }, []);

    // Paragrafen: herlaad wanneer hoofdstuk verandert
    useEffect(() => {
        const params = hoofdstukFilter ? `?hoofdstuk_nummer=${hoofdstukFilter}` : '';
        apiService.get(`/bepaling/paragrafen/${params}`)
            .then(response => setParagrafen(response.data))
            .catch(err => console.error('Error loading paragrafen:', err));
    }, [hoofdstukFilter]);

    // Artikelen: herlaad wanneer hoofdstuk of paragraaf verandert
    useEffect(() => {
        const queryParams = new URLSearchParams();
        if (hoofdstukFilter) queryParams.append('hoofdstuk_nummer', hoofdstukFilter);
        if (paragraafFilter) queryParams.append('paragraaf_nummer', paragraafFilter);
        const params = queryParams.toString() ? `?${queryParams.toString()}` : '';

        apiService.get(`/bepaling/artikelen/${params}`)
            .then(response => setArtikelen(response.data))
            .catch(err => console.error('Error loading artikelen:', err));
    }, [hoofdstukFilter, paragraafFilter]);

    // Leden: herlaad wanneer hoofdstuk, paragraaf of artikel verandert
    useEffect(() => {
        const queryParams = new URLSearchParams();
        if (hoofdstukFilter) queryParams.append('hoofdstuk_nummer', hoofdstukFilter);
        if (paragraafFilter) queryParams.append('paragraaf_nummer', paragraafFilter);
        if (artikelFilter) queryParams.append('artikel_nummer', artikelFilter);
        const params = queryParams.toString() ? `?${queryParams.toString()}` : '';

        apiService.get(`/bepaling/leden/${params}`)
            .then(response => setLeden(response.data))
            .catch(err => console.error('Error loading leden:', err));
    }, [hoofdstukFilter, paragraafFilter, artikelFilter]);

    // FIXED: Jaarversie is verplaatst naar de pagina zelf (bepalingen-list.tsx), zodat de
    // volgorde op het scherm "Jaarversie, Beschrijving, Hoofdstuk, Paragraaf, Artikel, Lid"
    // kan zijn — Beschrijving zit niet in dit component, dus Jaarversie kon hier niet meer
    // vóór Beschrijving staan zolang hij hier ook stond. De 4 resterende filters passen nu op
    // 1 regel: lg=3 x 4 = 12, exact de breedte van de (geneste) rij die dit component krijgt.
    return (
        <Row>
            <Col lg={3} md={3} sm={6} className="mb-2">
                <Form.Label>Hoofdstuk</Form.Label>
                <Form.Select
                    size="sm"
                    value={hoofdstukFilter}
                    onChange={e => onHoofdstukChange(e.target.value)}
                >
                    <option value="">Alle</option>
                    {hoofdstukken.map(h => (
                        <option key={h.nummer} value={h.nummer}>
                            {h.nummer} {h.titel ? `- ${h.titel}` : ''}
                        </option>
                    ))}
                </Form.Select>
            </Col>

            <Col lg={3} md={3} sm={6} className="mb-2">
                <Form.Label>Paragraaf</Form.Label>
                <Form.Select
                    size="sm"
                    value={paragraafFilter}
                    onChange={e => onParagraafChange(e.target.value)}
                >
                    <option value="">Alle</option>
                    {paragrafen.map(p => (
                        <option key={p.nummer} value={p.nummer}>
                            {p.nummer} {p.titel ? `- ${p.titel}` : ''}
                        </option>
                    ))}
                </Form.Select>
            </Col>

            <Col lg={3} md={3} sm={6} className="mb-2">
                <Form.Label>Artikel</Form.Label>
                <Form.Select
                    size="sm"
                    value={artikelFilter}
                    onChange={e => onArtikelChange(e.target.value)}
                >
                    <option value="">Alle</option>
                    {artikelen.map(a => (
                        <option key={a.nummer} value={a.nummer}>
                            {a.nummer} {a.titel ? `- ${a.titel}` : ''}
                        </option>
                    ))}
                </Form.Select>
            </Col>

            <Col lg={3} md={3} sm={6} className="mb-2">
                <Form.Label>Lid</Form.Label>
                <Form.Select
                    size="sm"
                    value={lidFilter}
                    onChange={e => onLidChange(e.target.value)}
                >
                    <option value="">Alle</option>
                    {leden.map(l => (
                        <option key={l.nummer} value={l.nummer}>
                            {l.nummer}
                        </option>
                    ))}
                </Form.Select>
            </Col>
        </Row>
    );
};

export default BepalingFilters;
